import { ReactNode, useRef, useState, useMemo, createElement } from 'react';
import styled from 'styled-components';
import { ReactComponent as CriticalAssetIcon } from '@elementary/common-ui/assets/16x16/critical-asset-tag-color.svg';
import { ReactComponent as ArrowIcon } from '@elementary/common-ui/assets/arrow-down-expand.svg';
import { ReactComponent as FivetranIcon } from '@elementary/common-ui/assets/fivetran-icon.svg';
import { ReactComponent as ColumnsIcon } from '@elementary/common-ui/assets/columns.svg';
import { ReactComponent as DbtIcon } from '@elementary/common-ui/assets/dbt-icon.svg';
import { ReactComponent as PythonIcon } from '@elementary/common-ui/assets/python-lineage.svg';
import { ReactComponent as ElementIcon } from '@elementary/common-ui/assets/element-icon.svg';
import { ReactComponent as HexIcon } from '@elementary/common-ui/assets/hex-icon-black.svg';
import { ReactComponent as LookerIcon } from '@elementary/common-ui/assets/looker-icon.svg';
import { ReactComponent as PowerBiIcon } from '@elementary/common-ui/assets/powerbi-icon.svg';
import { ReactComponent as SigmaIcon } from '@elementary/common-ui/assets/sigma-icon.svg';
import { ReactComponent as LightdashIcon } from '@elementary/common-ui/assets/lightdash-icon.svg';
import { ReactComponent as TableauIcon } from '@elementary/common-ui/assets/tableau-icon.svg';
import { ReactComponent as ThoughtSpotIcon } from '@elementary/common-ui/assets/thoughtspot-icon.svg';
import {
  Chip,
  Flex,
  FlexColumn,
  RestrictedIndicator,
  SearchField,
} from '@elementary/common-ui/components';
import OverflowTooltip from '@elementary/common-ui/components/OverflowTooltip';
import useHover from '@elementary/common-ui/hooks/useHover';
import {
  capitalize,
  pluralizeString,
} from '@elementary/common-ui/utils/common';
import { ErrorOutline } from '@mui/icons-material';
import { Tooltip, Typography, CircularProgress, useTheme } from '@mui/material';
import { Position, NodeProps, useEdges, useStore } from 'reactflow';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import { getWarehouseIcon } from '@elementary/common-ui/utils/environments';
import { ReactComponent as SnowflakeIcon } from '@elementary/common-ui/assets/data-warehouses/snowflake.svg';
import { fontSize, radius, space } from '../../style/utils';
import {
  GRAPH_NODE_HEIGHT,
  RESTRICTED_NODE_HEIGHT,
  GRAPH_PARENT_NODE_HEIGHT,
  GRAPH_NODE_CHILDREN_HEADER_HEIGHT,
  GRAPH_NODE_SEARCH_AREA_HEIGHT,
  CHILD_NODE_PADDING,
  LINEAGE_PER_PARENT_COLUMNS_LIMIT,
  LINEAGE_NODE_DETAIL_LABELS,
} from './constants';
import { LineageNodeColors, PlatformType, ReactFlowNodeData } from './types';
import { useLineageGraphContext } from './LineageGraphContext';
import GraphNodeCompact from './GraphNodeCompact';
import GraphNodeCompactRow from './GraphNodeCompactRow';
import GraphNodeHandle from './GraphNodeHandle';
import NodeContextMenu from './NodeContextMenu';
import useLineageNodeColors from './hooks/useLineageNodeColors';
import useNodeCriticalAsset from './hooks/useNodeCriticalAsset';
import {
  getFilterHighlightCss,
  getLineageNodeDetailText,
  getSelectedShadow,
  isFilterMatch,
} from './utils/lineageDisplaySettings';
import {
  getGraphNodeBorderColor,
  getGraphNodeChildrenState,
  selectLineageZoomTier,
} from './utils/lineageGraphUtils';

const CHILDREN_COUNT_GAP = '4.8px';

const semanticLayerIconMap: Record<string, ReactNode> = {
  snowflake: <SnowflakeIcon />,
};

const platformIconMap: Record<string, ReactNode> = {
  looker: <LookerIcon />,
  tableau: <TableauIcon />,
  powerbi: <PowerBiIcon />,
  sigma: <SigmaIcon />,
  thoughtspot: <ThoughtSpotIcon />,
  hex: <HexIcon />,
  lightdash: <LightdashIcon />,
};

enum ChildrenHeaderLabel {
  Element = 'element',
  Insight = 'insight',
  Cell = 'cell',
  ChartTile = 'chart tile',
  Column = 'column',
}

const childrenHeaderLabelMap = (
  node?: ReactFlowNodeData,
): ChildrenHeaderLabel | string => {
  const { platform, subType, type } = node?.metadata || {};

  if (type === 'semantic_view') {
    if (node?.parentNode) {
      return subType ?? ChildrenHeaderLabel.Element;
    }
    return ChildrenHeaderLabel.Element;
  }
  if (platform?.toLowerCase() === PlatformType.Sigma && subType === 'page') {
    return ChildrenHeaderLabel.Element;
  }
  if (
    platform?.toLowerCase() === PlatformType.ThoughtSpot &&
    subType === 'liveboard'
  ) {
    return ChildrenHeaderLabel.Insight;
  }
  if (platform?.toLowerCase() === PlatformType.Hex && subType === 'project') {
    return ChildrenHeaderLabel.Cell;
  }
  if (
    platform?.toLowerCase() === PlatformType.Lightdash &&
    subType === 'dashboard'
  ) {
    return ChildrenHeaderLabel.ChartTile;
  }
  return ChildrenHeaderLabel.Column;
};

const Wrapper = styled(Flex)<{
  $isHighlighted?: boolean;
  $isFilterMatch?: boolean;
  $selectedShadow?: string;
  $nodeColors: LineageNodeColors;
  $isExpanded?: boolean;
  $isOnHighlightPath?: boolean;
  $height: number;
  $width: number;
  $isParent?: boolean;
  $isRestricted?: boolean;
}>`
  background: ${({ theme }) => theme.palette.surface.base};
  height: ${({ $height, $isExpanded, $isParent, $isRestricted }) =>
    $isExpanded
      ? $height
      : $isRestricted
      ? RESTRICTED_NODE_HEIGHT
      : $isParent
      ? GRAPH_PARENT_NODE_HEIGHT + 4
      : GRAPH_NODE_HEIGHT}px;
  width: ${({ $width }) => $width}px;
  border-radius: ${radius('md')};
  overflow: hidden;

  border: ${({
    theme,
    $nodeColors,
    $isHighlighted,
    $isOnHighlightPath,
    $isRestricted,
  }) => {
    const isHighlighted = $isHighlighted || $isOnHighlightPath;
    return `${
      !isHighlighted && $isRestricted ? '2px dashed' : '2px solid'
    } ${getGraphNodeBorderColor(theme.palette, {
      isHighlighted,
      isRestricted: $isRestricted,
      colorByBorderColor: $nodeColors.border,
    })}`;
  }};

  ${({ theme, $isFilterMatch }) =>
    $isFilterMatch && getFilterHighlightCss(theme.palette)}
  ${({ $selectedShadow }) =>
    $selectedShadow && `box-shadow: ${$selectedShadow};`}
`;

const Header = styled(FlexColumn)<{
  $nodeColors: LineageNodeColors;
  $isRestricted?: boolean;
  $isSingleRow?: boolean;
}>`
  height: ${({ $isRestricted }) =>
    $isRestricted ? RESTRICTED_NODE_HEIGHT : GRAPH_NODE_HEIGHT}px;
  justify-content: ${({ $isSingleRow }) =>
    $isSingleRow ? 'center' : 'space-between'};
  padding: ${space('mdPlus', 'lg')};
  background: ${({ theme, $nodeColors, $isRestricted }) =>
    $isRestricted ? theme.palette.surface.subtle : $nodeColors.background};
`;

const NodeDetails = styled(Flex)`
  align-items: center;
  justify-content: flex-end;
  gap: ${space('sm')};
`;

const TypeWrapper = styled(Flex)`
  flex: 1;
  align-items: center;
  gap: ${space('sm')};
  min-width: 0;

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }
`;

const NodeTitle = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  gap: ${space('xs')};
`;

const ChildrenHeader = styled(Flex)<{
  $isExpanded?: boolean;
  $nodeColors: LineageNodeColors;
  $disabled?: boolean;
}>`
  height: ${GRAPH_NODE_CHILDREN_HEADER_HEIGHT}px;
  align-items: center;
  padding: ${space(0, 'lg')};
  gap: ${space('sm')};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  ${({ theme, $isExpanded }) =>
    $isExpanded && `background: ${theme.palette.surface.muted}`};

  &:hover {
    background: ${({ theme, $disabled }) =>
      $disabled ? 'inherit' : theme.palette.surface.muted};
  }

  border-top: 1px solid ${({ $nodeColors }) => $nodeColors.border};
`;

const ChildrenCountWrapper = styled(Flex)`
  align-items: center;
  gap: ${CHILDREN_COUNT_GAP};
  min-width: 0;
  flex: 1;
`;

const ArrowIconStyled = styled(ArrowIcon)<{ $flip: boolean }>`
  width: 10px;
  margin-top: ${space('hairline')};
  transform: ${({ $flip }) => ($flip ? 'rotate(-180deg)' : undefined)};
  transition: transform 0.1s;
`;

const ColumnIconStyled = styled(ColumnsIcon)`
  width: 16px;
  height: 21px;
`;

const DbtIconStyled = styled(DbtIcon)`
  path {
    fill: ${({ theme }) => theme.palette.icon.tertiary};
  }
`;

const ColumnLimitWarningIcon = styled(ErrorOutline)`
  width: 14px;
  height: 14px;
  color: ${({ theme }) => theme.palette.status.warning.main};
`;

const ColumnsLoadingArea = styled(FlexColumn)`
  height: ${GRAPH_NODE_SEARCH_AREA_HEIGHT}px;
  justify-content: center;
  align-items: center;
  padding: 0 ${CHILD_NODE_PADDING}px;
`;

const GraphNodeCard = (node: NodeProps<ReactFlowNodeData>) => {
  const { id, data, dragging } = node;
  const edges = useEdges();
  const hasPermission = useEnvPermission();

  const hasIncomingEdges = edges.some((e) => e.target === id && !e.hidden);
  const hasOutgoingEdges = edges.some((e) => e.source === id && !e.hidden);

  const {
    highlightedNode,
    exceededColumnLimitAssets,
    isControlled,
    columnFilterIds,
    columnSearchByNodeId,
    displaySettings,
  } = useLineageGraphContext();
  const theme = useTheme();

  const ref = useRef<any>();
  const childrenHeaderRef = useRef<any>();
  const searchFieldRef = useRef<any>();
  const isHover = useHover(ref);
  const isChildrenHeaderHover = useHover(childrenHeaderRef);
  const isSearchFieldHover = useHover(searchFieldRef);

  const [menuPos, setMenuPos] = useState<{
    left: number;
    top: number;
  } | null>(null);

  const hasTestReadScope = hasPermission('VIEW_TESTS');

  const handleMenuOpen = (e: any) => {
    if (isControlled) return;
    e.stopPropagation();
    e.preventDefault();
    setMenuPos({ left: e.clientX, top: e.clientY });
  };

  const handleMenuClose = () => {
    setMenuPos(null);
  };

  const isChildrenFilteredByColumnFilter =
    !!columnFilterIds && !columnFilterIds.has(id);

  const nodeTypeLabel = useMemo(() => {
    if (data.metadata.type === 'semantic_view' && data.parentNode) {
      return 'element';
    }
    return data.metadata.subType || data.metadata.type || 'model';
  }, [data]);

  const isChildWithChildren = !!node.data.parentNode && !!node.data.children;

  const childrenHeaderLabel = childrenHeaderLabelMap(data);

  const childrenHeaderIcon =
    data.metadata.subType === 'page' ||
    data.metadata.subType === 'project' ||
    (data.metadata.subType === 'dashboard' &&
      data.metadata.platform?.toLowerCase() === PlatformType.Lightdash) ? (
      <ElementIcon width={16} />
    ) : (
      <ColumnIconStyled />
    );

  const isElement =
    data.metadata.subType === 'element' ||
    data.metadata.subType === 'cell' ||
    data.metadata.subType === 'chart_tile';

  const isCell = data.metadata.subType === 'cell';

  const { isCriticalAsset, toggleCriticalAsset, hasSetPermissions } =
    useNodeCriticalAsset(id);

  const nodeIcon = useMemo(() => {
    if (isElement || isCell) return <ElementIcon />;

    if (data.metadata.type === 'semantic_view') {
      const platform =
        data.metadata.platform || data.parentNode?.platform || '';
      return semanticLayerIconMap[platform.toLowerCase()] ?? null;
    }

    const platformIcon =
      platformIconMap[data.metadata.platform?.toLowerCase() || ''];
    if (platformIcon) return platformIcon;

    if (data.metadata?.sourceType === 'python_sdk') {
      return <PythonIcon />;
    }

    if (data.metadata?.sourceType?.toLowerCase() === 'fivetran') {
      return <FivetranIcon />;
    }

    if (data.metadata?.sourceType && data.metadata?.sourceType !== 'dbt') {
      const platformIcon = createElement(
        getWarehouseIcon(data.metadata.dataPlatform?.toLowerCase() || ''),
      );
      if (platformIcon) return platformIcon;
    }

    return <DbtIconStyled />;
  }, [data.metadata, data.parentNode?.platform, isElement, isCell]);

  const nodeColors = useLineageNodeColors(data);
  const { nodeMetadata, detailField } = displaySettings;
  const showCriticalAsset = nodeMetadata.criticalAsset && isCriticalAsset;
  const showDetailLine = detailField !== 'none';
  const showNodeDetails = showDetailLine || showCriticalAsset;
  const detailText =
    detailField === 'none'
      ? ''
      : detailField === 'assetType'
      ? capitalize(nodeTypeLabel)
      : getLineageNodeDetailText(detailField, data.metadata);
  const detailTooltip =
    detailField === 'assetType'
      ? ''
      : `${LINEAGE_NODE_DETAIL_LABELS[detailField]}: ${
          detailText || 'not set'
        }`;
  const isRestricted = data.metadata.isRestricted === true;
  const isSelected = highlightedNode?.id === id;
  const hasExceededColumnLimit = exceededColumnLimitAssets.has(id);

  const {
    showConnectedColumnsOnly,
    childrenCount,
    isExpandable,
    showChildrenSection,
    isVisuallyExpanded,
  } = getGraphNodeChildrenState(data);

  // Highlight only filters column children (not elements), so flag as filtered
  // only when children are columns and fewer are shown than the full count.
  const fullColumnCount = data.columnsCount ?? data.metadata.columnsCount ?? 0;
  const isChildrenFilteredByHighlightColumn =
    showConnectedColumnsOnly &&
    childrenHeaderLabel === ChildrenHeaderLabel.Column &&
    childrenCount < fullColumnCount;

  const isChildrenFiltered =
    isChildrenFilteredByColumnFilter || isChildrenFilteredByHighlightColumn;

  // While loading, no children are merged yet so data.height collapses —
  // reserve room for the header, columns header and spinner.
  const loadingExpandedHeight =
    GRAPH_NODE_HEIGHT +
    GRAPH_NODE_CHILDREN_HEADER_HEIGHT +
    GRAPH_NODE_SEARCH_AREA_HEIGHT +
    4;

  const wrapperHeight =
    data.isColumnsLoading && !data.children
      ? loadingExpandedHeight
      : data.height;

  return (
    <Wrapper
      ref={ref}
      $isHighlighted={id === highlightedNode?.id}
      $isFilterMatch={isFilterMatch(data)}
      $selectedShadow={
        isSelected ? getSelectedShadow(theme.palette) : undefined
      }
      $nodeColors={nodeColors}
      $height={wrapperHeight}
      $isExpanded={isVisuallyExpanded}
      $isOnHighlightPath={data.isOnHighlightPath}
      $width={data.width}
      $isParent={showChildrenSection}
      $isRestricted={isRestricted}
      onContextMenu={isControlled ? undefined : handleMenuOpen}
    >
      <GraphNodeHandle
        type="source"
        position={Position.Right}
        $extendHandle={hasOutgoingEdges && isChildWithChildren}
        $isHighlighted={id === highlightedNode?.id || data.isOnHighlightPath}
        $nodeHeight={isRestricted ? RESTRICTED_NODE_HEIGHT : GRAPH_NODE_HEIGHT}
      />

      <FlexColumn
        sx={{
          height: '100%',
          width: '100%',
        }}
      >
        <Header
          $nodeColors={nodeColors}
          $isRestricted={isRestricted}
          $isSingleRow={!showNodeDetails}
        >
          {isRestricted ? (
            <RestrictedIndicator />
          ) : (
            <>
              {showNodeDetails && (
                <NodeDetails>
                  {showDetailLine && (
                    <TypeWrapper>
                      {nodeIcon}
                      <Tooltip
                        title={detailTooltip}
                        placement="top"
                        disableInteractive
                      >
                        <Typography
                          noWrap
                          sx={{
                            fontSize: fontSize('sm'),
                            lineHeight: 'normal',
                            color: detailText ? undefined : 'content.secondary',
                          }}
                        >
                          {detailText || '—'}
                        </Typography>
                      </Tooltip>
                    </TypeWrapper>
                  )}

                  {showCriticalAsset && <CriticalAssetIcon />}
                </NodeDetails>
              )}
              <NodeTitle>
                <OverflowTooltip
                  color="content.primary"
                  bold={isSelected}
                  typographySx={{ lineHeight: 'normal' }}
                >
                  {data.metadata.label}
                </OverflowTooltip>

                {hasTestReadScope &&
                  nodeMetadata.testResults &&
                  (!!data.totals?.errors ||
                    !!data.totals?.warnings ||
                    !!data.totals?.passed ||
                    !!data.totals?.failures) && (
                    <Flex sx={{ alignItems: 'center', gap: 0.6, ml: 3 }}>
                      {data.totals?.passed > 0 && (
                        <Chip
                          role="alert"
                          aria-label="success"
                          label={data.totals.passed}
                          color="success"
                          size="small"
                        />
                      )}
                      {data.totals?.warnings > 0 && (
                        <Chip
                          role="alert"
                          aria-label="warning"
                          label={data.totals.warnings}
                          color="warning"
                          size="small"
                        />
                      )}
                      {(data.totals?.errors > 0 ||
                        data.totals?.failures > 0) && (
                        <Chip
                          role="alert"
                          aria-label="error"
                          label={
                            (data.totals.errors || 0) +
                            (data.totals.failures || 0)
                          }
                          color="error"
                          size="small"
                        />
                      )}
                    </Flex>
                  )}
              </NodeTitle>

              {!isControlled && (
                <NodeContextMenu
                  showMenuButton={
                    isHover &&
                    !dragging &&
                    !isChildrenHeaderHover &&
                    !isSearchFieldHover
                  }
                  node={node}
                  pos={menuPos}
                  onOpen={handleMenuOpen}
                  onClose={handleMenuClose}
                  onHighlightNodeClick={() => ref.current?.click()}
                  isCriticalAsset={isCriticalAsset}
                  onSetIsCriticalAssets={toggleCriticalAsset}
                  showCriticalAssetOption={hasSetPermissions}
                  showTestResults={hasTestReadScope}
                />
              )}
            </>
          )}
        </Header>

        {showChildrenSection && (
          <FlexColumn>
            <ChildrenHeader
              ref={childrenHeaderRef}
              id={isExpandable ? 'expand-button' : 'children-header-static'}
              $disabled={!isExpandable}
              $isExpanded={isVisuallyExpanded}
              $nodeColors={nodeColors}
            >
              {childrenHeaderIcon}
              <ChildrenCountWrapper>
                <Typography noWrap>
                  {pluralizeString(
                    childrenCount,
                    `${
                      isChildrenFiltered ? 'filtered ' : ''
                    }${childrenHeaderLabel}`,
                    `${
                      isChildrenFiltered ? 'filtered ' : ''
                    }${childrenHeaderLabel}s`,
                  )}
                </Typography>
                {hasExceededColumnLimit && (
                  <Tooltip
                    title={`This node has too many columns. Showing up to ${LINEAGE_PER_PARENT_COLUMNS_LIMIT} columns.`}
                  >
                    <ColumnLimitWarningIcon />
                  </Tooltip>
                )}
              </ChildrenCountWrapper>
              {isExpandable && (
                <ArrowIconStyled
                  id="expand-button-icon"
                  $flip={isVisuallyExpanded}
                />
              )}
            </ChildrenHeader>

            {data.isColumnsLoading && (
              <ColumnsLoadingArea>
                <CircularProgress size={16} />
              </ColumnsLoadingArea>
            )}

            {data.isExpanded && !data.isColumnsLoading && (
              <FlexColumn
                ref={searchFieldRef}
                sx={{
                  height: `${GRAPH_NODE_SEARCH_AREA_HEIGHT}px`,
                  justifyContent: 'center',
                  paddingX: `${CHILD_NODE_PADDING}px`,
                }}
              >
                <SearchField
                  id={JSON.stringify({
                    elementId: 'search-field',
                    nodeId: node.id,
                  })}
                  defaultValue={columnSearchByNodeId.current[node.id]}
                  fullWidth
                />
              </FlexColumn>
            )}
          </FlexColumn>
        )}
      </FlexColumn>

      <GraphNodeHandle
        type="target"
        position={Position.Left}
        $extendHandle={hasIncomingEdges && isChildWithChildren}
        $isHighlighted={id === highlightedNode?.id || data.isOnHighlightPath}
        $nodeHeight={isRestricted ? RESTRICTED_NODE_HEIGHT : GRAPH_NODE_HEIGHT}
      />
    </Wrapper>
  );
};

const GraphNode = (node: NodeProps<ReactFlowNodeData>) => {
  const { isControlled } = useLineageGraphContext();
  const zoomTier = useStore(selectLineageZoomTier);

  if (isControlled || zoomTier === 'card') {
    return <GraphNodeCard {...node} />;
  }

  if (node.data.parentNode) {
    return <GraphNodeCompactRow {...node} />;
  }

  return <GraphNodeCompact {...node} tier={zoomTier} />;
};

export default GraphNode;
